import React, { memo } from 'react';
import PropTypes from 'prop-types';
import { Card, CardBody } from 'reactstrap';
import { map, size } from 'lodash';
import { UncontrolledTooltip } from 'reactstrap';
import { useTranslation } from 'react-i18next';

import { formatDate } from '../../../../utils';

import { statusColorClass } from '../../../../constants/index';
import UserAvatar from 'components/UserAvatar';

const COMPLEXITY_CLASSES = ['priority--low', 'priority--medium', 'priority--high'];
const PRIORITY_CLASSES = {
  low: 'priority--low',
  normal: 'priority--medium',
  high: 'priority--high',
  urgent: 'priority--urgent',
};
const PRIORITY_LABELS = {
  low: 'Low',
  normal: 'Normal',
  high: 'High',
  urgent: 'Urgent',
};
const PRIORITY_INITIALS = {
  low: 'L',
  normal: 'N',
  high: 'H',
  urgent: 'U',
};

const KanbanCard = ({ data, action }) => {
  const { t: translate } = useTranslation();

  const hasChecklist = data?.checklists?.length > 0;
  const checklistLength = data?.checklists?.length;
  const completedChecklistItems = data?.checklists.filter((item) => item.resolved).length;

  const dueDate = data?.due_date
    ? formatDate(new Date(+data.due_date), translate('date-enUS'))
    : 'N/A';

  const focusesObj = data?.custom_fields?.find((field) => field.name === 'Foco');
  const focusOptions = focusesObj?.type_config?.options;
  const focuses = focusesObj?.value?.map((focusId) => {
    return focusOptions?.find((option) => option.id === focusId);
  });

  const complexityObj = data?.custom_fields?.find((field) => field.name === 'Complexidade');
  const complexityOptions = complexityObj?.type_config?.options;
  const complexity = complexityOptions?.[complexityObj?.value]?.name;

  const impact = data?.priority?.priority;

  const statusObj = data?.custom_fields?.find((field) => field.name === 'Status');
  const statusOptions = statusObj?.type_config?.options;
  const status = statusOptions?.[statusObj?.value]?.name;

  const assigness = data?.assignees;

  const clientResponsibleObj = data?.custom_fields?.find(
    (field) => field.name === 'Responsável do Cliente',
  );
  const clientResponsibleOptions = clientResponsibleObj?.type_config?.options;
  const clientResponsibles = clientResponsibleObj?.value?.map((id) => {
    return clientResponsibleOptions?.find((option) => option.id === id);
  });

  return (
    <React.Fragment key={data?.id}>
      <Card onClick={() => action(data)} className="card-responsive">
        <CardBody className="borad-width">
          <div className="float-end ms-2 d-flex flex-column align-items-end gap-1">
            {hasChecklist ? (
              <div className="d-flex flex-row justify-content-center align-items-center gap-1">
                <p style={{ margin: 0, color: '#b4b4b4' }}>
                  ({completedChecklistItems}/{checklistLength})
                </p>
                <box-icon type="regular" name="list-ul" color="#b4b4b4" size="20px" />
              </div>
            ) : null}
          </div>
          <div>
            <h5 className="font-size-15">{data?.name ? data.name : ''}</h5>
          </div>
          <div className="mb-3">
            <div
              className="d-flex align-items-center"
              style={{ lineHeight: '12px', fontSize: '12px', color: '#ADB5BD' }}
            >
              <box-icon type="regular" name="calendar-alt" color="#ADB5BD" size="15px" />
              <span className="info--text">{dueDate}</span>
            </div>
            <div
              className="info--row d-flex align-items-center"
              style={{ lineHeight: '12px', fontSize: '12px', color: '#ADB5BD' }}
            >
              <box-icon type="regular" name="purchase-tag-alt" color="#ADB5BD" size="15px" />
              <span className="info--text" id={`focus-${data?.id}`}>
                {focuses?.length && focuses?.length > 3
                  ? `${focuses
                      ?.slice(0, 3)
                      ?.map((focus) => focus.label)
                      ?.join(', ')} +${focuses?.length - 3}`
                  : focuses?.map((focus) => focus.label)?.join(', ')}
                {!focuses?.length ? 'N/A' : null}
              </span>
              {focuses?.length && (
                <UncontrolledTooltip
                  defaultOpen={false}
                  flip
                  placement="right"
                  target={`focus-${data?.id}`}
                >
                  {focuses?.map((focus) => focus.label)?.join(', ')}
                </UncontrolledTooltip>
              )}
            </div>
            <div
              className="d-flex align-items-center"
              style={{ lineHeight: '20px', fontSize: '12px', color: '#ADB5BD' }}
            >
              <box-icon type="regular" name="user-circle" color="#ADB5BD" size="15px" />
              <span className="info--text" id={`client-responsible-${data?.id}`}>
                {clientResponsibles?.length && clientResponsibles?.length > 3
                  ? `${clientResponsibles?.length}+`
                  : clientResponsibles?.map((responsible) => responsible?.label)?.join(', ')}
              </span>
            </div>
          </div>

          <div className="avatar-element">
            <div className="avatar-group">
              {size(assigness) === 0 ? (
                <div className="avatar-group-item">
                  <div className="avatar-xs">
                    <UserAvatar userId={null} usePlaceholder={true} />
                  </div>
                </div>
              ) : (
                map(assigness, (member, index) => {
                  if (index >= 2) return;

                  return (
                    <div className="avatar-group-item" key={index}>
                      <img
                        src={member?.profilePicture}
                        alt={`${translate('Photo of')} ${member.username}`}
                        id={`card-${member?.id}-${data?.id}`}
                        width="36"
                        height="36"
                        style={{
                          borderRadius: '9999px',
                          aspectRatio: '1/1',
                          backgroundClip: 'padding-box',
                          backgroundSize: 'cover',
                          backgroundPosition: 'center center',
                        }}
                      />
                      <UncontrolledTooltip
                        defaultOpen={false}
                        flip
                        placement="right"
                        target={`card-${member?.id}-${data?.id}`}
                      >
                        {member.username}
                      </UncontrolledTooltip>
                    </div>
                  );
                })
              )}
              {size(assigness) > 2 && (
                <div className="avatar-group-item" id={`more-${data?.id}`}>
                  <div className="avatar-xs">
                    <span
                      className="avatar-title rounded-circle text-white font-size-16"
                      style={{ backgroundColor: 'black' }}
                    >
                      {`${size(assigness) - 2}+`}
                    </span>
                  </div>
                  <UncontrolledTooltip
                    defaultOpen={false}
                    flip
                    placement="right"
                    target={`more-${data?.id}`}
                  >
                    {assigness
                      .slice(2)
                      .map((member) => member.username)
                      .join(', ')}
                  </UncontrolledTooltip>
                </div>
              )}
            </div>
            <div className="third-row">
              <div className="priority-element">
                {complexity && (
                  <>
                    <div
                      className={`priority ${COMPLEXITY_CLASSES[complexityObj?.value]}`}
                      id={`complexity-${data?.id}`}
                    >
                      {complexity.slice(0, 1).toUpperCase()}
                    </div>
                    <UncontrolledTooltip
                      defaultOpen={false}
                      flip
                      placement="right"
                      target={`complexity-${data?.id}`}
                    >
                      {complexity}
                    </UncontrolledTooltip>
                  </>
                )}

                {impact && (
                  <>
                    <div
                      className={`priority ${PRIORITY_CLASSES[impact]}`}
                      id={`impact-${data?.id}`}
                    >
                      {translate(PRIORITY_INITIALS[impact])}
                    </div>
                    <UncontrolledTooltip
                      defaultOpen={false}
                      flip
                      placement="right"
                      target={`impact-${data?.id}`}
                    >
                      {translate(PRIORITY_LABELS[impact])}
                    </UncontrolledTooltip>
                  </>
                )}
              </div>

              {status && (
                <span
                  className={`status ${
                    statusColorClass[statusObj?.value]
                  } badge rounded-pill font-size-12`}
                >
                  {status}
                </span>
              )}
            </div>
          </div>
        </CardBody>
      </Card>
    </React.Fragment>
  );
};

KanbanCard.propTypes = {
  data: PropTypes.object,
  dragging: PropTypes.any,
  action: PropTypes.func,
};

const propsAreEqual = (prevProps, nextProps) => {
  const isActionEquals = prevProps.action === nextProps.action;
  const isDataEquals = JSON.stringify(prevProps.data) === JSON.stringify(nextProps.data);

  return isActionEquals && isDataEquals;
};

export default memo(KanbanCard, propsAreEqual);
